import React from 'react';
import PropTypes from 'prop-types';
import fecha from 'fecha';

import styled from 'styled-components';
import Text from '../../Text';
import { ReactComponent as CompleteIcon } from '../../../assets/icons/complete-icon.svg';
import { ReactComponent as ErrorIcon } from '../../../assets/icons/error-icon.svg';
import { ReactComponent as LinkIcon } from '../../../assets/icons/mainmenu/link.svg';

const ReasonChecklistRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  margin-bottom: 10px;
`;

const FieldLabel = styled.span`
  display: block;
  width: 220px;
  flex-shrink: 0;
  text-align: left;
  color: var(--black);
  font-size: 15px;
`;

const FieldValue = styled.span`
  color: ${({ $isInvalid }) => ($isInvalid ? 'var(--red)' : 'var(--gray)')};
  font-size: 15px;
  word-break: break-word;
`;

const FieldValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FieldContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const FieldReason = styled.span`
  margin-top: 4px;
  font-size: 12px;
  color: var(--red);
`;

const HelpText = styled.a`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  color: var(--gray);
  text-decoration: none;

  > span {
    text-decoration: none;
    color: var(--gray);
    margin-top: 10px;
    font-size: 15px;
  }

  > svg {
    transform: translateY(4px);
    margin-right: 10px;
    width: 12px;
    height: 12px;
    fill: var(--gray);
  }
`;

const isFieldInvalid = field => {
  if (field === 'on') {
    return true;
  }

  if (field && typeof field === 'object') {
    return field.valid === false;
  }

  return false;
};

const isFieldValid = field => {
  if (field === 'off') {
    return true;
  }

  if (field && typeof field === 'object') {
    return field.valid === true;
  }

  return false;
};

const renderFieldStatusIcon = field => {
  if (isFieldInvalid(field)) {
    return <ErrorIcon fill="var(--red)" width="14px" height="14px" />;
  }

  if (isFieldValid(field)) {
    return <CompleteIcon fill="var(--brand-green)" width="14px" height="14px" />;
  }

  return null;
};

const getFieldValue = field => {
  if (field && typeof field === 'object' && Object.prototype.hasOwnProperty.call(field, 'value')) {
    return field.value || '-';
  }

  if (typeof field === 'string' && field !== 'on' && field !== 'off') {
    return field;
  }

  return '-';
};

const getFieldReason = field => {
  if (field && typeof field === 'object' && typeof field.comment === 'string') {
    const reason = field.comment.trim();
    return reason ? `Powód: ${field.comment.trim()}` : '';
  }

  return '';
};

const ProfileRejectedNotification = ({ notification, isCompany }) => {
  const payload = (notification && notification.payload) || {};
  const adminFeedback = payload.adminFeedback || {};
  const rejectedAt = payload.at || (notification && notification.created_at);
  const renderFieldRow = (label, field) => {
    const invalid = isFieldInvalid(field);

    return (
      <ReasonChecklistRow>
        <FieldLabel>{label}</FieldLabel>
        <FieldContent>
          <FieldValueRow>
            {renderFieldStatusIcon(field)}
            <FieldValue $isInvalid={invalid}>{getFieldValue(field)}</FieldValue>
          </FieldValueRow>
          {invalid && <FieldReason>{getFieldReason(field)}</FieldReason>}
        </FieldContent>
      </ReasonChecklistRow>
    );
  };

  return (
    <>
      <Text size={15} margin="0 0 40px">
        Dane które przesłałeś zawierają parę nieprawidłowości. Zaznaczyliśmy je poniżej. Popraw je
        wysyłając wniosek ponownie.
      </Text>

      {renderFieldRow(isCompany ? 'Nazwa firmy:' : 'Imię i nazwisko:', adminFeedback.fullname)}

      {isCompany && renderFieldRow('NIP:', adminFeedback.personalNumber)}

      {renderFieldRow('Ulica:', adminFeedback.addressStreet)}

      {renderFieldRow('Miasto:', adminFeedback.addressCity)}

      {renderFieldRow('Kod pocztowy:', adminFeedback.addressPostalCode)}

      {renderFieldRow('Kraj:', adminFeedback.addressCountry)}

      {renderFieldRow('Numer konta bankowego:', adminFeedback.bankNumber)}

      {renderFieldRow('Link do Profilu:', adminFeedback.socialMediaLink)}

      <Text>
        Odrzucono:&nbsp;
        {fecha.format(new Date(rejectedAt), 'HH:mm DD/MM/YY')}
      </Text>

      <Text margin="45px 0 0px 0">W razie pytań bądź wątpliwości</Text>
      <HelpText target="_blank" rel="noopener noreferrer" href="https://fb.com/tipplypl">
        <LinkIcon />
        <span>skontaktuj się z nami!</span>
      </HelpText>
    </>
  );
};

export default ProfileRejectedNotification;

ProfileRejectedNotification.propTypes = {
  notification: PropTypes.instanceOf(Object).isRequired,
  isCompany: PropTypes.bool.isRequired,
};
